import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PagePath } from '@monorepo/client-types';
import { PurchaseStatus, PurchaseStatusResponse } from '@monorepo/types';
import { usePurchaseStatus } from '@hub/api/queries/use-purchase-queries';

interface UsePurchaseStatusPollingOptions {
  snapshotId: string;
  enabled?: boolean;
  timeoutMs?: number;
  onError?: (error: Error) => void;
}

interface UsePurchaseStatusPollingReturn {
  data: PurchaseStatusResponse | undefined;
  status: PurchaseStatus | undefined;
  isLoading: boolean;
  purchaseId: string | undefined;
  isError: boolean;
  error: Error | null;
  isPolling: boolean;
  timeRemaining: number;
  totalProgress: number;
}
const DEFAULT_TIMEOUT = 1 * 35 * 1000; // 35 seconds

export const usePurchaseStatusPolling = ({
  snapshotId,
  enabled = true,
  timeoutMs = DEFAULT_TIMEOUT,
  onError,
}: UsePurchaseStatusPollingOptions): UsePurchaseStatusPollingReturn => {
  const navigate = useNavigate();
  const [isPolling, setIsPolling] = useState(enabled);
  const [timeRemaining, setTimeRemaining] = useState(timeoutMs);
  const [startTime] = useState(() => Date.now());
  const [totalProgress, setTotalProgress] = useState(0);

  const {
    data: statusData,
    isLoading,
    isError,
    error,
  } = usePurchaseStatus(snapshotId, isPolling);

  const currentStatus = statusData?.status;

  useEffect(() => {
    if (currentStatus) {
      switch (currentStatus) {
        case PurchaseStatus.Completed:
          setIsPolling(false);
          break;
        case PurchaseStatus.Failed:
        case PurchaseStatus.Canceled:
          setIsPolling(false);
          break;
        case PurchaseStatus.Pending:
          break;
      }
    }
  }, [currentStatus]);

  // Handle errors
  useEffect(() => {
    if (isError && error && onError) {
      onError(error);
    }
  }, [isError, error, onError]);

  // Simplified single timeout and progress management
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, timeoutMs - elapsed);

      // Simple linear progress calculation
      const progress = Math.min((elapsed / timeoutMs) * 100, 100);

      setTimeRemaining(remaining);
      setTotalProgress(progress);

      // Handle timeout
      if (remaining <= 0) {
        setIsPolling(false);
        setTotalProgress(100);
        // Default timeout behavior: redirect to overview
        navigate(`${PagePath.Overview}`);
      }
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [enabled, isPolling, startTime, timeoutMs, navigate]);

  return {
    data: statusData,
    status: currentStatus,
    isLoading,
    purchaseId: statusData?.purchaseId || undefined,
    isError,
    error,
    isPolling,
    timeRemaining,
    totalProgress,
  };
};
